import React from 'react';
import { API_BASE } from '../services/api';

// ── Helpers ────────────────────────────────────────────────────────────────────
const fmt    = (n) => '$' + Number(n || 0).toLocaleString('es-CO', { minimumFractionDigits: 2 });
const fmtPct = (n) => Number(n || 0).toFixed(2) + '%';

function fmtFecha(f) {
  if (!f) return '—';
  const d = new Date(f);
  if (isNaN(d)) return f;
  return d.toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function formatNit(nit) {
  if (!nit) return '—';
  if (nit.includes('-')) return nit;
  return nit.replace(/\D/g, '');
}

// ── Leyendas obligatorias (Numeral 1 / Art. 11 Res. 000042) ───────────────────
function leyendaTipo(tipo) {
  if (tipo === '91') return 'Esta es la representación gráfica de una Nota Débito Electrónica de Ajuste';
  if (tipo === '92') return 'Esta es la representación gráfica de una Nota Crédito Electrónica de Ajuste';
  return 'Esta es la representación gráfica de una Factura Electrónica de Venta';
}
function tituloTipo(tipo) {
  if (tipo === '91') return 'NOTA DÉBITO ELECTRÓNICA DE AJUSTE';
  if (tipo === '92') return 'NOTA CRÉDITO ELECTRÓNICA DE AJUSTE';
  return 'FACTURA ELECTRÓNICA DE VENTA';
}

// ── Medios de pago DIAN ────────────────────────────────────────────────────────
const MEDIOS_PAGO = {
  '10':'Efectivo','20':'Cheque','42':'Consignación bancaria',
  '47':'Transferencia / ACH','48':'Tarjeta crédito','49':'Tarjeta débito','1':'No definido',
};

// ── Resúmenes tributarios ──────────────────────────────────────────────────────
function resumenIVA(items) {
  const g = {};
  (items || []).forEach(it => {
    const pct = Number(it.iva_pct ?? 19);
    if (!g[pct]) g[pct] = { base: 0, iva: 0 };
    const base = Number(it.precio_unitario || 0) * Number(it.cantidad || 1) * (1 - Number(it.descuento_pct || 0) / 100);
    g[pct].base += base;
    g[pct].iva  += Number(it.iva_valor || 0);
  });
  return Object.entries(g).sort(([a], [b]) => b - a);
}
function resumenINC(items) {
  const g = {};
  (items || []).forEach(it => {
    const pct = Number(it.impoconsumo_pct || 0);
    if (pct === 0) return;
    if (!g[pct]) g[pct] = { base: 0, impo: 0 };
    const base = Number(it.precio_unitario || 0) * Number(it.cantidad || 1) * (1 - Number(it.descuento_pct || 0) / 100);
    g[pct].base += base;
    g[pct].impo += Number(it.impoconsumo_valor || 0);
  });
  return Object.entries(g).sort(([a], [b]) => b - a);
}

// ── Condición tributaria (Numeral 12) ─────────────────────────────────────────
function condicionesTributarias(cfg) {
  const lista = [];
  const resp  = (cfg.empresa_responsabilidades || '').toUpperCase();
  if (resp.includes('GRAN CONTRIBUYENTE') || resp.includes('O-13')) lista.push('Gran Contribuyente');
  if (resp.includes('AUTORRETENEDOR')     || resp.includes('O-15')) lista.push('Autorretenedor del Impuesto sobre la Renta');
  if (resp.includes('AGENTE RETENEDOR')   || resp.includes('O-23')) lista.push('Agente de Retención en el Impuesto sobre las Ventas');
  if (resp.includes('SIMPLE')             || resp.includes('O-47')) lista.push('Contribuyente del Régimen SIMPLE de Tributación');
  return lista;
}

// ── Componente principal ───────────────────────────────────────────────────────
export default function FacturaDIANDoc({ datos }) {
  if (!datos) return null;

  const d    = datos;
  const cfg  = d._config || {};
  const tipo = d.tipo_documento_fe || '01';
  const items = d.items || [];

  // El CUFE/QR solo se muestran cuando la DIAN ya registró (aceptó) el documento.
  const registrada   = d.estado_fe === 'aceptada';
  const codigo       = registrada ? (d.cufe || d.cude || '') : '';
  const qrUrl        = codigo ? `https://catalogo-vpfe.dian.gov.co/document/searchqr?documentkey=${codigo}` : '';
  const codigoLabel  = tipo === '92' || tipo === '91' ? 'CUDE' : 'CUFE';
  const gruposIVA    = resumenIVA(items);
  const gruposINC    = resumenINC(items);
  const medioPagoDesc = d.medio_pago_desc || MEDIOS_PAGO[d.medio_pago_codigo] || 'Transferencia';
  const condTrib     = condicionesTributarias(cfg);
  const hayINC       = items.some(it => Number(it.impoconsumo_pct || 0) > 0);
  const esPruebas    = cfg.fe_ambiente !== '1';

  return (
    <div style={s.page}>

      {/* ── Banda AMBIENTE DE PRUEBAS ─────────────────────────────────────────── */}
      {esPruebas && (
        <div style={s.pruebaBand}>
          ⚠️ DOCUMENTO DE PRUEBA — AMBIENTE DE HABILITACIÓN (ProfileExecutionID=2) — NO TIENE VALIDEZ FISCAL
        </div>
      )}

      {/* ── Leyenda superior (Numeral 1) ─────────────────────────────────────── */}
      <div style={s.leyendaTop}>{leyendaTipo(tipo)}</div>

      {/* ── Encabezado: Empresa + Número de documento ────────────────────────── */}
      <div style={s.header}>
        <div style={s.headerLeft}>
          {cfg.empresa_logo_url && (
            <img src={`${API_BASE}${cfg.empresa_logo_url}`} alt="Logo"
              style={{ maxHeight: 60, maxWidth: 160, marginBottom: 6, objectFit: 'contain' }} />
          )}
          {cfg.empresa_nombre && (
            <div style={s.empresaNombre}>{cfg.empresa_nombre.toUpperCase()}</div>
          )}
          {cfg.empresa_nit && (
            <div style={s.empresaDetalle}>NIT: {formatNit(cfg.empresa_nit)}</div>
          )}
          {cfg.empresa_regimen && (
            <div style={s.empresaDetalle}>{cfg.empresa_regimen}</div>
          )}
          {cfg.empresa_tipo_persona && (
            <div style={s.empresaDetalle}>{cfg.empresa_tipo_persona}</div>
          )}
          {cfg.empresa_responsabilidades && (
            <div style={s.empresaDetalle}>Responsab.: {cfg.empresa_responsabilidades}</div>
          )}
          {cfg.empresa_ciiu && (
            <div style={s.empresaDetalle}>CIIU: {cfg.empresa_ciiu}</div>
          )}
          {cfg.empresa_direccion && (
            <div style={s.empresaDetalle}>{cfg.empresa_direccion}</div>
          )}
          {cfg.empresa_ciudad && (
            <div style={s.empresaDetalle}>{cfg.empresa_ciudad}</div>
          )}
          {cfg.empresa_telefono && (
            <div style={s.empresaDetalle}>Tel: {cfg.empresa_telefono}</div>
          )}
          {cfg.empresa_email && (
            <div style={s.empresaDetalle}>{cfg.empresa_email}</div>
          )}
        </div>

        <div style={s.headerRight}>
          <div style={s.docTipo}>{tituloTipo(tipo)}</div>
          <div style={s.docNumero}>{d.numero}</div>
          <div style={s.docFecha}>Fecha: {fmtFecha(d.fecha_emision || d.created_at)}</div>
          {d.hora_factura && <div style={s.docFecha}>Hora: {d.hora_factura}</div>}
        </div>
      </div>

      {/* ── Resolución DIAN (Numeral 4) ──────────────────────────────────────── */}
      {cfg.resolucion_numero && (
        <div style={s.resolucionBox}>
          <span style={s.resLabel}>Resolución DIAN N°&nbsp;</span>
          <span style={s.resVal}>{cfg.resolucion_numero}</span>
          {cfg.resolucion_fecha && (
            <><span style={s.resSep}> del </span><span style={s.resVal}>{fmtFecha(cfg.resolucion_fecha)}</span></>
          )}
          {cfg.resolucion_prefijo && (
            <><span style={s.resSep}>&nbsp;· Prefijo:</span><span style={s.resVal}>&nbsp;{cfg.resolucion_prefijo}</span></>
          )}
          {cfg.resolucion_desde && cfg.resolucion_hasta && (
            <><span style={s.resSep}>&nbsp;· Rango autorizado:</span><span style={s.resVal}>&nbsp;{cfg.resolucion_desde} – {cfg.resolucion_hasta}</span></>
          )}
          {cfg.resolucion_fecha_hasta && (
            <><span style={s.resSep}>&nbsp;· Vigente hasta:</span><span style={s.resVal}>&nbsp;{fmtFecha(cfg.resolucion_fecha_hasta)}</span></>
          )}
        </div>
      )}

      {/* ── Condición tributaria del emisor (Numeral 12) ─────────────────────── */}
      {condTrib.length > 0 && (
        <div style={s.condTribBox}>
          <span style={s.condTribLabel}>Condición tributaria del emisor:&nbsp;</span>
          {condTrib.map((c, i) => (
            <span key={i} style={s.condTribItem}>{c}{i < condTrib.length - 1 ? ' · ' : ''}</span>
          ))}
        </div>
      )}

      {/* ── Adquiriente + Condiciones de pago (Numerales 3, 10, 11) ─────────── */}
      <div style={s.dosPaneles}>
        <div style={s.panel}>
          <div style={s.panelTitle}>ADQUIRIENTE / DESTINATARIO</div>
          <Row label="Razón social / Nombre" val={d.cliente_nombre} bold />
          <Row label="Identificación"
            val={[d.tipo_documento, d.nit, d.digito_verificacion ? `DV ${d.digito_verificacion}` : ''].filter(Boolean).join(' · ')} />
          {d.cliente_regimen      && <Row label="Régimen"       val={d.cliente_regimen} />}
          {d.cliente_tipo_persona && <Row label="Tipo persona"  val={d.cliente_tipo_persona} />}
          {d.cliente_direccion    && <Row label="Dirección"     val={d.cliente_direccion} />}
          {(d.cliente_municipio || d.cliente_ciudad) && (
            <Row label="Ciudad / Municipio"
              val={[d.cliente_municipio, d.cliente_ciudad, d.cliente_departamento].filter(Boolean).join(', ')} />
          )}
          {d.cliente_email && <Row label="Correo" val={d.cliente_email} />}
        </div>

        <div style={s.panel}>
          <div style={s.panelTitle}>CONDICIONES DE PAGO</div>
          <Row label="Forma de pago" val={d.forma_pago === 'credito' ? 'Crédito' : 'Contado'} />
          <Row label="Medio de pago"
            val={`${medioPagoDesc}${d.medio_pago_codigo ? ` (cód. ${d.medio_pago_codigo})` : ''}`} />
          {Number(d.condiciones_pago_dias) > 0 && (
            <Row label="Plazo" val={`${d.condiciones_pago_dias} días`} />
          )}
          {d.fecha_vencimiento && <Row label="Vence" val={fmtFecha(d.fecha_vencimiento)} />}
          {d.proyecto_nombre   && <Row label="Proyecto" val={d.proyecto_nombre} />}
          {d.referencia_oc     && <Row label="OC / Ref. cliente" val={d.referencia_oc} bold />}
        </div>
      </div>

      {/* ── OC destacada ─────────────────────────────────────────────────────── */}
      {d.referencia_oc && (
        <div style={s.ocBox}>
          <span style={s.ocLabel}>📋 Orden de Compra / Referencia del cliente:&nbsp;</span>
          <span style={s.ocVal}>{d.referencia_oc}</span>
        </div>
      )}

      {/* ── Referencia documento origen (solo notas débito/crédito) ──────────── */}
      {(tipo === '91' || tipo === '92') && (
        <div style={s.referenciaBox}>
          <div style={s.refTitle}>
            {tipo === '91' ? '📈 REFERENCIA FACTURA AJUSTADA (NOTA DÉBITO)' : '📉 REFERENCIA FACTURA AJUSTADA (NOTA CRÉDITO)'}
          </div>
          <div style={s.refGrid}>
            <div><span style={s.refLabel}>Número factura origen:</span><span style={s.refVal}>&nbsp;{d.ref_numero || '—'}</span></div>
            <div><span style={s.refLabel}>Fecha:</span><span style={s.refVal}>&nbsp;{fmtFecha(d.ref_fecha)}</span></div>
            {d.concepto_nota && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={s.refLabel}>Concepto:</span><span style={s.refVal}>&nbsp;{d.concepto_nota}</span>
              </div>
            )}
            {(d.ref_cufe || d.ref_cude) && (
              <div style={{ gridColumn: '1 / -1' }}>
                <span style={s.refLabel}>CUFE factura origen:</span>
                <span style={{ ...s.refVal, fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 9 }}>
                  &nbsp;{d.ref_cufe || d.ref_cude}
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Tabla de ítems (Numeral 8) ───────────────────────────────────────── */}
      {items.length > 0 && (
        <table style={s.table}>
          <thead>
            <tr style={s.thead}>
              <th style={{ ...s.th, width: 24, textAlign: 'center' }}>#</th>
              <th style={s.th}>Descripción del bien o servicio</th>
              <th style={{ ...s.th, textAlign: 'center', width: 46 }}>Cant.</th>
              <th style={{ ...s.th, width: 44 }}>Unid.</th>
              <th style={{ ...s.th, textAlign: 'right', width: 88 }}>P. Unitario</th>
              <th style={{ ...s.th, textAlign: 'right', width: 52 }}>Dto.%</th>
              <th style={{ ...s.th, textAlign: 'right', width: 88 }}>Base neta</th>
              <th style={{ ...s.th, textAlign: 'right', width: 46 }}>IVA%</th>
              <th style={{ ...s.th, textAlign: 'right', width: 80 }}>IVA $</th>
              {hayINC && <th style={{ ...s.th, textAlign: 'right', width: 46 }}>INC%</th>}
              {hayINC && <th style={{ ...s.th, textAlign: 'right', width: 80 }}>INC $</th>}
              <th style={{ ...s.th, textAlign: 'right', width: 96 }}>Total ítem</th>
            </tr>
          </thead>
          <tbody>
            {items.map((it, i) => {
              const base = Number(it.precio_unitario || 0) * Number(it.cantidad || 1)
                         * (1 - Number(it.descuento_pct || 0) / 100);
              const incVal = Number(it.impoconsumo_valor || 0);
              return (
                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                  <td style={{ ...s.td, textAlign: 'center', color: '#64748b' }}>{i + 1}</td>
                  <td style={s.td}>{it.descripcion}</td>
                  <td style={{ ...s.td, textAlign: 'center' }}>{it.cantidad}</td>
                  <td style={s.td}>{it.unidad || 'und'}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmt(it.precio_unitario)}</td>
                  <td style={{ ...s.td, textAlign: 'right', color: Number(it.descuento_pct) > 0 ? '#f59e0b' : '#94a3b8' }}>
                    {Number(it.descuento_pct) > 0 ? fmtPct(it.descuento_pct) : '—'}
                  </td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmt(base)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmtPct(it.iva_pct ?? 19)}</td>
                  <td style={{ ...s.td, textAlign: 'right' }}>{fmt(it.iva_valor)}</td>
                  {hayINC && (
                    <td style={{ ...s.td, textAlign: 'right', color: incVal > 0 ? '#9a3412' : '#94a3b8' }}>
                      {incVal > 0 ? fmtPct(it.impoconsumo_pct) : '—'}
                    </td>
                  )}
                  {hayINC && (
                    <td style={{ ...s.td, textAlign: 'right', color: incVal > 0 ? '#9a3412' : '#94a3b8' }}>
                      {incVal > 0 ? fmt(incVal) : '—'}
                    </td>
                  )}
                  <td style={{ ...s.td, textAlign: 'right', fontWeight: 600 }}>{fmt(it.total_item)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}

      {/* ── Totales + Resumen tributario (Numerales 9, 13) ───────────────────── */}
      <div style={s.totalesArea}>
        {/* Resumen tributario por tarifa */}
        <div style={s.resumenTrib}>
          <div style={s.resumenTitle}>RESUMEN TRIBUTARIO (Art. 11 Num. 13)</div>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 10.5 }}>
            <thead>
              <tr style={{ background: '#e2e8f0' }}>
                <th style={{ padding: '5px 8px', textAlign: 'left', fontWeight: 600 }}>Impuesto</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>Base gravable</th>
                <th style={{ padding: '5px 8px', textAlign: 'right', fontWeight: 600 }}>Valor</th>
              </tr>
            </thead>
            <tbody>
              {gruposIVA.map(([pct, { base, iva }]) => (
                <tr key={`iva-${pct}`}>
                  <td style={{ padding: '4px 8px' }}>IVA {fmtPct(pct)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmt(base)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmt(iva)}</td>
                </tr>
              ))}
              {gruposINC.map(([pct, { base, impo }]) => (
                <tr key={`inc-${pct}`} style={{ background: '#fff7ed' }}>
                  <td style={{ padding: '4px 8px', color: '#9a3412' }}>INC {fmtPct(pct)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>{fmt(base)}</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: '#9a3412' }}>{fmt(impo)}</td>
                </tr>
              ))}
              {Number(d.retefuente) > 0 && (
                <tr style={{ background: '#fffbeb' }}>
                  <td style={{ padding: '4px 8px', color: '#92400e' }}>(-) Retención en la fuente</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>—</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: '#92400e' }}>-{fmt(d.retefuente)}</td>
                </tr>
              )}
              {Number(d.reteica) > 0 && (
                <tr style={{ background: '#fffbeb' }}>
                  <td style={{ padding: '4px 8px', color: '#92400e' }}>(-) Retención ICA</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right' }}>—</td>
                  <td style={{ padding: '4px 8px', textAlign: 'right', color: '#92400e' }}>-{fmt(d.reteica)}</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Columna de totales (Numeral 9) */}
        <div style={s.totalesCol}>
          <TotalRow label="Subtotal (base imponible)" val={fmt(d.subtotal)} />
          <TotalRow label="Total IVA"                 val={fmt(d.iva)} />
          {Number(d.impoconsumo) > 0 && (
            <TotalRow label="INC (Impoconsumo)" val={fmt(d.impoconsumo)} color="#9a3412" />
          )}
          {Number(d.retefuente) > 0 && (
            <TotalRow label="(-) Retención en la fuente" val={`-${fmt(d.retefuente)}`} color="#92400e" />
          )}
          {Number(d.reteica) > 0 && (
            <TotalRow label="(-) Retención ICA" val={`-${fmt(d.reteica)}`} color="#92400e" />
          )}
          <TotalRow label="TOTAL A PAGAR" val={fmt(d.total)} final />
          {Number(d.saldo) > 0 && Number(d.saldo) < Number(d.total) && (
            <TotalRow label="Saldo pendiente" val={fmt(d.saldo)} color="#ef4444" />
          )}
        </div>
      </div>

      {/* ── CUFE / CUDE + QR (Numerales 15 y 16) ────────────────────────────── */}
      {codigo && (
        <div style={s.cufeBox}>
          <div style={s.cufeInner}>
            <div style={s.cufeLabel}>{codigoLabel} — Código Único de Factura Electrónica (Art. 11 Num. 15)</div>
            <div style={s.cufeVal}>{codigo}</div>
            <div style={s.cufeUrl}>Verifique en: {qrUrl}</div>
          </div>
          {d._qrDataUrl && (
            <div style={s.qrWrap}>
              <img src={d._qrDataUrl} alt="QR DIAN" style={s.qrImg} />
              <div style={s.qrLabel}>Escanee para<br />verificar en DIAN</div>
            </div>
          )}
        </div>
      )}

      {/* ── Observaciones ────────────────────────────────────────────────────── */}
      {d.notas && (
        <div style={s.notasBox}>
          <strong>Observaciones: </strong>{d.notas}
        </div>
      )}

      {/* ── Leyenda inferior (Numeral 1) ─────────────────────────────────────── */}
      <div style={s.leyendaBottom}>{leyendaTipo(tipo)}</div>

      {/* ── Leyenda valor legal XML ──────────────────────────────────────────── */}
      <div style={s.leyendaXml}>
        El documento XML validado por la DIAN es el documento electrónico con plena validez legal y fiscal.
        Esta representación gráfica no tiene valor legal por sí sola. Res. 000042/2020 · Res. 000165/2023.
      </div>

      {/* ── Información del software (Numeral 18) ────────────────────────────── */}
      <div style={s.softwareBox}>
        <div style={s.softwareRow}>
          <span style={s.softwareLabel}>Software facturación:</span>
          <span style={s.softwareVal}>ERP Ingeniería</span>
        </div>
        <div style={s.softwareRow}>
          <span style={s.softwareLabel}>Proveedor tecnológico:</span>
          <span style={s.softwareVal}>{cfg.empresa_nombre || 'ERP Ingeniería'}</span>
        </div>
        <div style={s.softwareRow}>
          <span style={s.softwareLabel}>Generado:</span>
          <span style={s.softwareVal}>{new Date().toLocaleString('es-CO')}</span>
        </div>
      </div>
    </div>
  );
}

// ── Sub-componentes ────────────────────────────────────────────────────────────
function Row({ label, val, bold }) {
  return (
    <div style={s.row}>
      <span style={s.rowLabel}>{label}:</span>
      <span style={{ ...s.rowVal, fontWeight: bold ? 600 : 400 }}>{val || '—'}</span>
    </div>
  );
}

function TotalRow({ label, val, final, color }) {
  return (
    <div style={{ ...s.totalRow, ...(final ? s.totalFinal : {}), ...(color ? { color } : {}) }}>
      <span>{label}</span><span>{val}</span>
    </div>
  );
}

// ── Estilos ────────────────────────────────────────────────────────────────────
const s = {
  page:           { fontFamily: 'Arial, sans-serif', fontSize: 11, color: '#1e293b', padding: 0, maxWidth: 850 },

  // Banda de pruebas
  pruebaBand:     { background:'#fef08a', color:'#713f12', fontSize: 10, fontWeight: 700, textAlign: 'center', padding: '6px 12px', letterSpacing: .3, borderBottom: '2px solid #eab308' },

  // Leyendas obligatorias
  leyendaTop:     { background: '#1e3a8a', color: '#fff', fontSize: 9.5, textAlign: 'center', padding: '5px 12px', fontWeight: 600, letterSpacing: .4 },
  leyendaBottom:  { background: '#f0f9ff', border: '1px solid #bae6fd', color: '#0369a1', fontSize: 9.5, textAlign: 'center', padding: '5px 12px', fontWeight: 600, letterSpacing: .4, marginTop: 10 },
  leyendaXml:     { background: '#fefce8', border: '1px solid #fde68a', color: '#78350f', fontSize: 8.5, textAlign: 'center', padding: '5px 12px', marginTop: 4 },

  // Encabezado
  header:         { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '3px solid #1e3a8a', padding: '14px 0 12px', marginBottom: 10 },
  headerLeft:     { flex: 1 },
  headerRight:    { textAlign: 'right', minWidth: 220 },
  empresaNombre:  { fontSize: 15, fontWeight: 700, color: '#1e3a8a', marginBottom: 3 },
  empresaDetalle: { fontSize: 10, color: '#475569', lineHeight: 1.5 },
  docTipo:        { fontSize: 10, color: '#64748b', textTransform: 'uppercase', letterSpacing: 1.5, marginBottom: 2 },
  docNumero:      { fontSize: 22, fontWeight: 700, color: '#1e3a8a' },
  docFecha:       { fontSize: 11, color: '#475569', marginTop: 2 },

  // Resolución DIAN
  resolucionBox:  { background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 4, padding: '6px 12px', fontSize: 10, color: '#475569', marginBottom: 8 },
  resLabel:       { fontWeight: 600, color: '#334155' },
  resVal:         { color: '#1e3a8a', fontWeight: 600 },
  resSep:         { color: '#94a3b8' },

  // Condición tributaria (Numeral 12)
  condTribBox:    { background: '#fef9c3', border: '1px solid #fde047', borderRadius: 4, padding: '5px 12px', fontSize: 10, marginBottom: 8 },
  condTribLabel:  { fontWeight: 700, color: '#713f12' },
  condTribItem:   { color: '#78350f' },

  // Paneles adquiriente / pago
  dosPaneles:     { display: 'flex', gap: 12, marginBottom: 10 },
  panel:          { flex: 1, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, padding: '10px 12px' },
  panelTitle:     { fontSize: 9.5, fontWeight: 700, color: '#1e3a8a', textTransform: 'uppercase', letterSpacing: .8, marginBottom: 6, borderBottom: '1px solid #e2e8f0', paddingBottom: 4 },
  row:            { display: 'flex', gap: 6, marginBottom: 3 },
  rowLabel:       { fontSize: 10, color: '#94a3b8', flexShrink: 0, minWidth: 90 },
  rowVal:         { fontSize: 10, color: '#1e293b' },

  // OC
  ocBox:          { background: '#eff6ff', border: '1px solid #93c5fd', borderRadius: 6, padding: '7px 14px', marginBottom: 10, display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 4 },
  ocLabel:        { fontSize: 10, fontWeight: 700, color: '#1e40af', textTransform: 'uppercase', letterSpacing: .4 },
  ocVal:          { fontSize: 11, fontWeight: 700, color: '#1e3a8a', letterSpacing: .3 },

  // Referencia nota
  referenciaBox:  { background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 6, padding: '10px 14px', marginBottom: 10 },
  refTitle:       { fontSize: 10, fontWeight: 700, color: '#92400e', textTransform: 'uppercase', letterSpacing: .6, marginBottom: 6 },
  refGrid:        { display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '4px 16px' },
  refLabel:       { fontSize: 10, fontWeight: 600, color: '#78350f' },
  refVal:         { fontSize: 10, color: '#1e293b' },

  // Tabla ítems
  table:          { width: '100%', borderCollapse: 'collapse', marginBottom: 0, fontSize: 10 },
  thead:          { background: '#1e3a8a' },
  th:             { padding: '7px 6px', color: '#fff', fontSize: 9.5, textAlign: 'left', fontWeight: 600 },
  td:             { padding: '6px 6px', borderBottom: '1px solid #f1f5f9', fontSize: 10 },

  // Totales
  totalesArea:    { display: 'flex', gap: 16, marginTop: 6, marginBottom: 12, alignItems: 'flex-start' },
  resumenTrib:    { flex: 1, border: '1px solid #e2e8f0', borderRadius: 6, overflow: 'hidden' },
  resumenTitle:   { background: '#334155', color: '#fff', fontSize: 9.5, fontWeight: 700, padding: '5px 8px', textTransform: 'uppercase', letterSpacing: .6 },
  totalesCol:     { minWidth: 240, display: 'flex', flexDirection: 'column', gap: 1 },
  totalRow:       { display: 'flex', justifyContent: 'space-between', padding: '4px 0', borderBottom: '1px solid #f1f5f9', fontSize: 11, color: '#475569' },
  totalFinal:     { fontWeight: 700, fontSize: 14, color: '#1e3a8a', borderTop: '2px solid #1e3a8a', borderBottom: 'none', paddingTop: 6, marginTop: 4 },

  // CUFE + QR
  cufeBox:        { display: 'flex', gap: 12, alignItems: 'flex-start', background: '#f0f9ff', border: '1px solid #bae6fd', borderRadius: 6, padding: '10px 14px', marginBottom: 10 },
  cufeInner:      { flex: 1 },
  cufeLabel:      { fontSize: 9.5, fontWeight: 700, color: '#0369a1', textTransform: 'uppercase', letterSpacing: .8, marginBottom: 4 },
  cufeVal:        { fontFamily: 'monospace', wordBreak: 'break-all', fontSize: 9, color: '#1e293b', lineHeight: 1.6 },
  cufeUrl:        { fontSize: 8, color: '#64748b', marginTop: 4, wordBreak: 'break-all' },
  qrWrap:         { textAlign: 'center', flexShrink: 0 },
  qrImg:          { width: 90, height: 90, display: 'block' },  // 90px ≈ 2.4cm @ 96dpi — cumple mín. 2cm DIAN
  qrLabel:        { fontSize: 7.5, color: '#64748b', marginTop: 2 },

  // Observaciones
  notasBox:       { background: '#fefce8', borderLeft: '4px solid #fbbf24', padding: '8px 14px', borderRadius: '0 6px 6px 0', fontSize: 10.5, color: '#78350f', marginBottom: 10 },

  // Software (Numeral 18)
  softwareBox:    { display: 'flex', gap: 24, justifyContent: 'center', flexWrap: 'wrap', borderTop: '1px solid #e2e8f0', paddingTop: 8, marginTop: 8 },
  softwareRow:    { display: 'flex', gap: 4 },
  softwareLabel:  { fontSize: 8.5, color: '#94a3b8', fontWeight: 600 },
  softwareVal:    { fontSize: 8.5, color: '#475569' },
};
